import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMin } from "@/lib/format";
import { SectionTitle } from "@/components/common";
import { AlertTriangle, TrendingUp } from "lucide-react";

const COUNT_TILES = [
  ["received", "Orders Received"],
  ["ready", "Orders Ready"],
  ["picking_in_progress", "Picking In Progress"],
  ["packing_in_progress", "Packing In Progress"],
  ["qc_pending", "QC Pending"],
  ["documents_pending", "Documents Pending"],
  ["vehicle_waiting", "Vehicle Waiting"],
  ["loading_in_progress", "Loading In Progress"],
  ["dispatched", "Dispatched Today"],
  ["delayed", "Delayed Orders"],
];

export default function ControlTower() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const res = await api.get("/fulfilment/dashboard");
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const { counts, kpis, delayed, predicted } = data;

  return (
    <div className="space-y-6" data-testid="control-tower-page">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Live · auto-refresh 30s</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight uppercase">Warehouse Control Tower</h1>
      </div>

      <div>
        <SectionTitle>Today</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {COUNT_TILES.map(([key, label]) => (
            <div key={key} className={`bg-card border rounded-lg p-4 ${key === "delayed" && counts.delayed > 0 ? "border-signal-red/60 bg-signal-red/5" : "border-border"}`} data-testid={`tower-${key}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
              <p className={`metric text-3xl font-extrabold mt-1 ${key === "delayed" && counts.delayed > 0 ? "text-signal-red" : ""}`}>{counts[key]}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle>KPIs</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["Order Fulfilment Time", fmtMin(kpis.order_fulfilment_minutes), "order → dispatch"],
            ["Warehouse Processing", fmtMin(kpis.warehouse_processing_minutes), "reserve → dispatch"],
            ["Picking Efficiency", kpis.picking_efficiency != null ? `${kpis.picking_efficiency}%` : "—", "std ÷ actual"],
            ["Packing Efficiency", kpis.packing_efficiency != null ? `${kpis.packing_efficiency}%` : "—", "std ÷ actual"],
            ["Loading Efficiency", kpis.loading_efficiency != null ? `${kpis.loading_efficiency}%` : "—", "std ÷ actual"],
            ["On-Time Dispatch", kpis.on_time_dispatch_pct != null ? `${kpis.on_time_dispatch_pct}%` : "—", "vs required date"],
          ].map(([label, value, sub]) => (
            <div key={label} className="bg-card border border-border rounded-lg p-4" data-testid={`kpi-${label.toLowerCase().replace(/\W+/g, "-")}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" />{label}</p>
              <p className="metric text-xl font-extrabold mt-1 text-primary">{value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={`rounded-lg border p-4 sm:p-5 ${delayed.length ? "border-signal-red/60 bg-signal-red/5" : "border-signal-green/40 bg-signal-green/5"}`} data-testid="delayed-orders-panel">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className={`w-5 h-5 ${delayed.length ? "text-signal-red animate-pulse-red" : "text-signal-green"}`} />
          <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Delayed Orders</h2>
          <span className="metric text-sm text-muted-foreground ml-auto">{delayed.length}</span>
        </div>
        {delayed.length === 0 ? (
          <p className="text-sm text-signal-green font-medium">No delayed orders right now.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="delayed-orders-table">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Order", "Customer", "Stage", "Responsible", "Std", "Actual", "Delay", "Reason"].map((h) => (
                    <th key={h} className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {delayed.map((d) => (
                  <tr key={d.bill_no} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`/orders/${d.bill_no}`)} data-testid={`delayed-order-${d.bill_no}`}>
                    <td className="px-3 py-2 metric font-bold text-primary">{d.bill_no}</td>
                    <td className="px-3 py-2">{d.customer}</td>
                    <td className="px-3 py-2 capitalize">{d.stage}</td>
                    <td className="px-3 py-2">{d.responsible}</td>
                    <td className="px-3 py-2 metric">{d.standard != null ? `${d.standard}m` : "—"}</td>
                    <td className="px-3 py-2 metric">{d.actual != null ? `${d.actual}m` : "—"}</td>
                    <td className="px-3 py-2 metric text-signal-red font-bold">{d.delay != null ? `+${d.delay}m` : "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {predicted.length > 0 && (
        <div className="rounded-lg border border-signal-amber/50 bg-signal-amber/5 p-4 sm:p-5" data-testid="predicted-delays-panel">
          <h2 className="text-xl font-bold uppercase tracking-tight mb-3 text-signal-amber">Predicted Delay Risk</h2>
          <ul className="space-y-2">
            {predicted.map((p) => (
              <li key={p.bill_no} className="text-sm flex flex-wrap gap-x-3 cursor-pointer hover:bg-accent/40 rounded px-2 py-1" onClick={() => navigate(`/orders/${p.bill_no}`)} data-testid={`predicted-${p.bill_no}`}>
                <span className="metric font-bold text-primary">{p.bill_no}</span>
                <span>{p.customer}</span>
                <span className="text-muted-foreground capitalize">stage: {p.stage}</span>
                <span className="metric text-signal-amber">needs ~{p.estimated_remaining_minutes}m, only {p.time_left_minutes}m left</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
